import { useCallback, useEffect, useMemo } from 'react'

import { useWallet } from '@/hooks/use-wallet'
import { TradeFilterStatus, TradeStatus } from '@/types'
import { SELECTED_ALL } from '@/utils'
import { IWallet } from '@/wallets'

import i18next from 'i18next'

import { useUrlParams } from '../use-url-params'

const PAGE_STATE_INIT = {
  page: 1,
  pageSize: 20,
} as const

export const TRADE_STATUS_MAP: Record<string, TradeStatus[]> = {
  [SELECTED_ALL]: [],
  [TradeFilterStatus.IN_PROCESS]: [
    TradeStatus.INIT,
    TradeStatus.PRE_SUBMIT,
    TradeStatus.DEPOSITED,
    TradeStatus.SUBMITTED_TO_SOLVER,
    TradeStatus.SUBMITTED,
    TradeStatus.DEPOSIT_CONFIRMED,
    TradeStatus.PMM_SELECTED,
    TradeStatus.PAYMENT_MADE,
    TradeStatus.PRE_REFUND,
    TradeStatus.RENEW_QUOTE,
  ],
  [TradeFilterStatus.COMPLETED]: [TradeStatus.SETTLEMENT_CONFIRMED, TradeStatus.PAYMENT_CONFIRMED],
  [TradeFilterStatus.EXPIRED]: [TradeStatus.TIMEOUT, TradeStatus.TRADE_TIMEOUT, TradeStatus.TIMEOUT_NO_DEPOSIT],
  [TradeFilterStatus.REFUNDED]: [TradeStatus.REFUNDED],
}

export interface WalletFilterOption {
  label: string
  value: string
  wallet: IWallet | null
}

export type TFilterStatusFilterOption = {
  label: string
  value: string
}

export const ADDRESS_FILTER_OPTIONS: WalletFilterOption[] = [
  {
    label: i18next.t('title.ALL_ADDRESSES'),
    value: SELECTED_ALL,
    wallet: null,
  },
]

export const STATUS_FILTER_OPTIONS: TFilterStatusFilterOption[] = [
  {
    label: i18next.t('title.ALL_STATUS'),
    value: SELECTED_ALL,
  },
  {
    label: i18next.t('title.SWAP_IN_PROGRESS'),
    value: TradeFilterStatus.IN_PROCESS,
  },
  {
    label: i18next.t('title.SWAP_COMPLETED'),
    value: TradeFilterStatus.COMPLETED,
  },
  {
    label: i18next.t('tradeStatus.REFUNDED'),
    value: TradeFilterStatus.REFUNDED,
  },
  {
    label: i18next.t('tradeStatus.SWAP_FAILED'),
    value: TradeFilterStatus.EXPIRED,
  },
]

const DEFAULT_ADDRESS = ADDRESS_FILTER_OPTIONS[0].value
const DEFAULT_STATUS = STATUS_FILTER_OPTIONS[0].value

export function useTradeFilters() {
  const { walletPubkeys, walletAddresses } = useWallet()
  const isConnected = walletAddresses.length > 0

  const [addressSelected, setAddressSelected] = useUrlParams({
    paramName: 'address',
    defaultValue: DEFAULT_ADDRESS,
  })

  const [statusSelected, setStatusSelected] = useUrlParams({
    paramName: 'status',
    defaultValue: DEFAULT_STATUS,
  })

  const [page, setPage] = useUrlParams({
    paramName: 'page',
    defaultValue: PAGE_STATE_INIT.page,
    parseValue: (value) => {
      const parsed = parseInt(value)
      return isNaN(parsed) ? PAGE_STATE_INIT.page : parsed
    },
  })

  const [pageSize, setPageSize] = useUrlParams({
    paramName: 'pageSize',
    defaultValue: PAGE_STATE_INIT.pageSize,
    parseValue: (value) => {
      const parsed = parseInt(value)
      return isNaN(parsed) ? PAGE_STATE_INIT.pageSize : parsed
    },
  })

  const fromAddress = useMemo(() => {
    const allWallets = walletPubkeys.flatMap((wallet) => [wallet.address, wallet.publicKey])
    if (addressSelected === SELECTED_ALL) return allWallets
    const currentWallet = walletPubkeys.find((wp) => wp.address === addressSelected)
    if (!currentWallet) return []
    return [currentWallet.address, currentWallet.publicKey]
  }, [walletPubkeys, addressSelected])

  const statuses = useMemo(() => TRADE_STATUS_MAP[statusSelected] || [], [statusSelected])

  const resetFilters = useCallback(() => {
    setPage(PAGE_STATE_INIT.page, { ignoreUpdateParams: true })
    setPageSize(PAGE_STATE_INIT.pageSize, { ignoreUpdateParams: true })
    setAddressSelected(DEFAULT_ADDRESS, { ignoreUpdateParams: true })
    setStatusSelected(DEFAULT_STATUS, { ignoreUpdateParams: true })
    window.history.pushState({}, '', window.location.pathname)
  }, [])

  const handleFilterChange = useCallback((value: string, setter: (value: string, options?: any) => void) => {
    setter(value, {
      extraParam: {
        name: 'page',
        value: PAGE_STATE_INIT.page.toString(),
      },
    })
    setPage(PAGE_STATE_INIT.page, {
      ignoreUpdateParams: true,
    })
  }, [])

  const handleAddressChange = useCallback(
    (data: WalletFilterOption) => handleFilterChange(data.value, setAddressSelected),
    [handleFilterChange]
  )

  const handleStatusChange = useCallback(
    (data: TFilterStatusFilterOption) => handleFilterChange(data.value, setStatusSelected),
    [handleFilterChange]
  )

  // Reset filters when wallet is disconnected
  useEffect(() => {
    if (isConnected) return
    resetFilters()
  }, [isConnected, resetFilters])

  // Reset to all wallets if the selected wallet is disconnected
  useEffect(() => {
    if (!isConnected || addressSelected === SELECTED_ALL || walletAddresses.includes(addressSelected)) return
    handleAddressChange(ADDRESS_FILTER_OPTIONS[0])
  }, [walletAddresses, isConnected, addressSelected, handleAddressChange])

  return {
    addressSelected,
    statusSelected,
    page,
    pageSize,
    fromAddress,
    statuses,
    handleAddressChange,
    handleStatusChange,
    setPage,
    setPageSize,
  }
}
